import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import RelatedResources from '../../../components/feature/RelatedResources';

export default function TaxRequirementsPage() {
  const taxTypes = [
    {
      title: 'Texas Sales and Use Tax',
      icon: 'ri-shopping-cart-line',
      description: 'Required for businesses selling tangible goods or taxable services in Texas',
      details: [
        {
          subtitle: 'Application Process',
          items: [
            'Register online through Texas Comptroller\'s Webfile system',
            'Provide business information and ownership details',
            'Specify business activities and estimated monthly sales',
            'Receive your Texas Taxpayer Number within 2-3 business days',
          ],
        },
        {
          subtitle: 'Tax Rates',
          items: [
            'State sales tax rate: 6.25%',
            'El Paso local sales tax: 2.00%',
            'Combined rate: 8.25%',
            'Special districts may have additional rates',
          ],
        },
        {
          subtitle: 'Filing Schedule',
          items: [
            'Monthly: For businesses with tax liability over $1,500/month',
            'Quarterly: For businesses with tax liability under $1,500/month',
            'Annual: For businesses with tax liability under $1,000/year',
            'Due on the 20th of the month following the reporting period',
          ],
        },
      ],
    },
    {
      title: 'Federal Tax Obligations',
      icon: 'ri-government-line',
      description: 'Federal taxes required by the IRS for all business entities',
      details: [
        {
          subtitle: 'Income Tax',
          items: [
            'Sole Proprietorship: Report on Schedule C of personal tax return',
            'Partnership: File Form 1065, partners receive K-1',
            'Corporation: File Form 1120 (C-Corp) or 1120-S (S-Corp)',
            'LLC: Taxed as sole proprietor, partnership, or corporation',
          ],
        },
        {
          subtitle: 'Employment Taxes',
          items: [
            'Federal Income Tax Withholding from employee wages',
            'Social Security and Medicare taxes (FICA)',
            'Federal Unemployment Tax (FUTA)',
            'File Form 941 quarterly for employment taxes',
          ],
        },
        {
          subtitle: 'Self-Employment Tax',
          items: [
            'Applies to sole proprietors and partners',
            'Covers Social Security and Medicare contributions',
            'Current rate: 15.3% on net earnings',
            'Pay quarterly estimated taxes using Form 1040-ES',
          ],
        },
      ],
    },
    {
      title: 'Local Property Taxes',
      icon: 'ri-home-4-line',
      description: 'Property taxes for business real estate and personal property in El Paso',
      details: [
        {
          subtitle: 'Property Tax Rates',
          items: [
            'El Paso County: Approximately 2.5% of assessed value',
            'City of El Paso: Additional municipal rate',
            'School districts: Varies by location',
            'Total effective rate typically 2.8-3.2%',
          ],
        },
        {
          subtitle: 'Assessment Process',
          items: [
            'El Paso Central Appraisal District (EPCAD) assesses property values',
            'Business personal property must be rendered annually',
            'Rendition deadline: April 15th each year',
            'Property owners can protest assessed values',
          ],
        },
        {
          subtitle: 'Payment Schedule',
          items: [
            'Tax bills mailed in October',
            'Payment due by January 31st',
            'Penalty and interest apply after due date',
            'Installment payment plans available for qualifying businesses',
          ],
        },
      ],
    },
  ];

  const industrySpecific = [
    {
      industry: 'Restaurants & Food Service',
      requirements: [
        'Mixed Beverage Tax (if serving alcohol)',
        'Food and Beverage Tax reporting',
        'Tip reporting requirements',
        'Health permit fees',
      ],
    },
    {
      industry: 'Retail Businesses',
      requirements: [
        'Sales tax permit required',
        'Inventory tax considerations',
        'Use tax on out-of-state purchases',
        'Resale certificate management',
      ],
    },
    {
      industry: 'Professional Services',
      requirements: [
        'Franchise tax (if applicable)',
        'Professional license fees',
        'No sales tax on most services',
        'Quarterly estimated tax payments',
      ],
    },
    {
      industry: 'Manufacturing',
      requirements: [
        'Sales tax exemption on manufacturing equipment',
        'Inventory tax exemptions available',
        'Environmental compliance fees',
        'Special depreciation schedules',
      ],
    },
    {
      industry: 'Construction',
      requirements: [
        'Contractor license fees',
        'Sales tax on materials',
        'Workers compensation insurance',
        'Surety bond requirements',
      ],
    },
    {
      industry: 'Healthcare',
      requirements: [
        'Professional license renewal fees',
        'Medical waste disposal fees',
        'HIPAA compliance costs',
        'Malpractice insurance premiums',
      ],
    },
  ];

  const taxDeadlines = [
    { month: 'January', deadline: '31st', description: 'Property tax payment due' },
    { month: 'January', deadline: '31st', description: 'Annual sales tax return (if applicable)' },
    { month: 'March', deadline: '15th', description: 'S-Corp and Partnership tax returns due' },
    { month: 'April', deadline: '15th', description: 'Business personal property rendition due' },
    { month: 'April', deadline: '15th', description: 'C-Corp tax returns due' },
    { month: 'April', deadline: '15th', description: 'Q1 estimated tax payment' },
    { month: 'June', deadline: '15th', description: 'Q2 estimated tax payment' },
    { month: 'September', deadline: '15th', description: 'Q3 estimated tax payment' },
    { month: 'October', deadline: '31st', description: 'S-Corp extension deadline' },
    { month: 'December', deadline: '31st', description: 'Year-end tax planning deadline' },
  ];

  const incentives = [
    {
      title: 'Foreign Trade Zone Benefits',
      description: 'El Paso\'s Foreign Trade Zone #68 offers duty deferral and reduction opportunities for international businesses.',
      benefits: ['Duty elimination on re-exports', 'Duty deferral on imports', 'Inverted tariff relief', 'Streamlined customs procedures'],
    },
    {
      title: 'Texas Enterprise Zone Program',
      description: 'State sales and use tax refunds for businesses creating jobs in designated enterprise zones.',
      benefits: ['Refund on qualified expenditures', 'Job creation incentives', 'Capital investment credits', 'Up to $2,500 per job created'],
    },
    {
      title: 'Research & Development Tax Credit',
      description: 'Federal and state credits for qualified research and development activities.',
      benefits: ['Federal credit up to 20% of qualified expenses', 'Texas franchise tax credit available', 'Carryforward provisions', 'Applies to various industries'],
    },
    {
      title: 'Work Opportunity Tax Credit',
      description: 'Federal tax credit for hiring individuals from targeted groups facing employment barriers.',
      benefits: ['Credit up to $9,600 per eligible employee', 'Applies to veterans, ex-felons, and others', 'Reduces federal income tax liability', 'No limit on number of hires'],
    },
  ];

  const resources = [
    {
      name: 'Texas Comptroller of Public Accounts',
      description: 'State tax registration, filing, and information',
      website: 'comptroller.texas.gov',
      phone: '(800) 252-5555',
    },
    {
      name: 'IRS Small Business & Self-Employed',
      description: 'Federal tax information and resources',
      website: 'irs.gov/businesses/small-businesses-self-employed',
      phone: '(800) 829-4933',
    },
    {
      name: 'El Paso Central Appraisal District',
      description: 'Property tax assessments and information',
      website: 'epcad.org',
      phone: '(915) 780-2222',
      address: '5801 Trowbridge Dr, El Paso, TX 79925',
    },
    {
      name: 'SCORE El Paso',
      description: 'Free tax planning and business counseling',
      website: 'elpaso.score.org',
      phone: '(915) 534-0541',
    },
  ];

  const relatedResources = [
    {
      title: 'Business Registration',
      description: 'Register your business and obtain your EIN before setting up tax accounts.',
      path: '/regulations/business-registration',
      icon: 'ri-file-list-3-line'
    },
    {
      title: 'Tax Registration Guide',
      description: 'Step-by-step instructions for registering with federal, state, and local tax authorities.',
      path: '/resources/tax-registration-guide',
      icon: 'ri-government-line'
    },
    {
      title: 'Financial Projections Guide',
      description: 'Plan for tax obligations with accurate financial forecasting and budgeting.',
      path: '/resources/financial-projections-guide',
      icon: 'ri-line-chart-line'
    }
  ];

  return (
    <>
      <SEOHead
        title="El Paso Business Tax Requirements & Registration Guide"
        description="Complete guide to tax requirements for El Paso businesses. Learn about federal EIN, Texas sales tax, franchise tax, employer taxes, and tax filing deadlines for Texas businesses."
        canonical="/regulations/tax-requirements"
        keywords="El Paso business taxes, Texas sales tax permit, EIN registration, Texas franchise tax, employer taxes Texas, business tax requirements"
        ogImage="https://public.readdy.ai/ai/img_res/d5ae2e23bbead1658484506973d9acc0.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main id="main-content" className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link 
                to="/regulations" 
                className="text-[#D2691E] hover:underline text-sm font-medium"
                aria-label="Return to Regulations page"
              >
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Tax Requirements",
                    "item": "https://elpasotexasbusiness.com/regulations/tax-requirements"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="mb-16">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">Tax Requirements</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Understanding your tax obligations is crucial for business success. This comprehensive guide covers federal, state, and local tax requirements for El Paso businesses.
              </p>
            </div>

            {/* Main Tax Types */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-10">Essential Tax Obligations</h2>
              <div className="space-y-12">
                {taxTypes.map((tax, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${tax.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{tax.title}</h3>
                        <p className="text-sm text-gray-600">{tax.description}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-8 ml-18">
                      {tax.details.map((detail, detailIndex) => (
                        <div key={detailIndex}>
                          <h4 className="text-lg font-bold text-gray-900 mb-4">{detail.subtitle}</h4>
                          <ul className="space-y-3">
                            {detail.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="flex items-start space-x-3">
                                <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <i className="ri-check-line text-green-600 text-sm"></i>
                                </div>
                                <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Industry-Specific Requirements */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Industry-Specific Tax Requirements</h2>
              <p className="text-sm text-gray-600 mb-10">Different industries have unique tax obligations beyond the standard requirements.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {industrySpecific.map((item, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{item.industry}</h3>
                    <ul className="space-y-3">
                      {item.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="flex items-start space-x-3">
                          <div className="w-5 h-5 bg-[#D2691E]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#D2691E] text-xs"></i>
                          </div>
                          <span className="text-sm text-gray-700 leading-relaxed">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Tax Deadlines Calendar */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Important Tax Deadlines</h2>
              <p className="text-sm text-gray-600 mb-10">Mark these critical dates on your calendar to avoid penalties and interest.</p>
              
              <div className="bg-[#FAFAF8] rounded-2xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {taxDeadlines.map((deadline, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-white rounded-xl">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-[#D2691E]/10 rounded-xl flex flex-col items-center justify-center">
                          <span className="text-xs font-semibold text-[#D2691E] uppercase">{deadline.month.substring(0, 3)}</span>
                          <span className="text-lg font-bold text-gray-900">{deadline.deadline}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{deadline.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tax Incentives */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Tax Incentives & Credits</h2>
              <p className="text-sm text-gray-600 mb-10">Take advantage of these programs to reduce your tax burden and support business growth.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {incentives.map((incentive, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{incentive.title}</h3>
                    <p className="text-sm text-gray-600 mb-6">{incentive.description}</p>
                    <ul className="space-y-3">
                      {incentive.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-green-600 text-sm"></i>
                          </div>
                          <span className="text-sm text-gray-700 leading-relaxed">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Resources */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Tax Resources & Contacts</h2>
              <p className="text-sm text-gray-600 mb-10">Connect with these organizations for tax assistance and information.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {resources.map((resource, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{resource.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">{resource.description}</p>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <i className="ri-global-line text-[#D2691E]"></i>
                        <span className="text-sm text-gray-700">{resource.website}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="ri-phone-line text-[#D2691E]"></i>
                        <span className="text-sm text-gray-700">{resource.phone}</span>
                      </div>
                      {resource.address && (
                        <div className="flex items-center space-x-2">
                          <i className="ri-map-pin-line text-[#D2691E]"></i>
                          <span className="text-sm text-gray-700">{resource.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-[#D2691E] to-[#A0522D] rounded-2xl p-12 text-center mb-16">
              <h2 className="text-3xl font-bold text-white mb-4">Need Help with Tax Planning?</h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                Our team can connect you with tax professionals and resources to ensure your business stays compliant and maximizes available benefits.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white text-[#D2691E] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                aria-label="Contact us for tax planning assistance"
              >
                Contact Us Today
              </Link>
            </div>
          </div>

          <RelatedResources resources={relatedResources} />
        </main>

        <Footer />
      </div>
    </>
  );
}